import React, {
	useLayoutEffect,
	useMemo,
	useCallback,
	memo,
	useRef,
} from "react";
import { shallowEqual } from "react-redux";

import { Order, Car } from "../../../services";
import { useTypedDispatch, useTypedSelector } from "../../../redux/store";
import orderPage from "../../../redux/reducers/OrdersPage";
import { ORDERS_TYPES, OrderType } from "../../../redux/constants/OrdersPage";
import { OrderStatus } from "../../../types";
import {
	useModelSubscribe,
	useCarParkFilterAccessControl,
	useTaxiServiceFilterAccessControl,
} from "../../../hooks";
import { notifyNewOnlineOrders } from "../../../utils/onlineOrderNotifier";
import resolveAllStage from "../../../utils/resolveAllStage";

const enrichOrderCars = (
	order: Order.Model,
	carMap: Map<number, Car.Model>,
): Order.Model => {
	if (!order.carToOrder?.length) return order;

	let changed = false;
	const carToOrder = order.carToOrder.map((carItem) => {
		const car = carMap.get(carItem.car?.id);
		if (!car) return carItem;
		changed = true;
		return {
			...carItem,
			car: {
				...carItem.car,
				park: car.park,
			},
		};
	});

	return changed ? { ...order, carToOrder } : order;
};

const OrdersDataLoader: React.FC<OrdersDataLoader.Props> = ({ carModels }) => {
	const dispatch = useTypedDispatch();
	const { subCarParkFilterAccess } =
		useCarParkFilterAccessControl<Order.Model>();
	const { subTaxiServiceFilterAccess } =
		useTaxiServiceFilterAccessControl<Order.Model>();

	const lang = useTypedSelector((state) => state.session.language);
	const connected = useTypedSelector((state) => state.prpc.connected);
	const authorized = useTypedSelector((state) => state.prpc.authorized);
	const ordersType = useTypedSelector(
		(state) => state.ordersPageReducer.ordersType,
	);
	const currentUserId = useTypedSelector((state) => state.account.user?.id);
	const timeToLive = useTypedSelector(
		(state) =>
			state.settings.order.preliminary.preliminaryOrderBecomesLive
				.interval,
	);
	const filtersBtns = useTypedSelector(
		(state) => state.ordersPageReducer.filtersBtns,
		shallowEqual,
	);
	const canSubscribe = useMemo(
		() => connected && authorized,
		[authorized, connected],
	);

	const carMap = useMemo(() => {
		const map = new Map<number, Car.Model>();
		for (const car of carModels) {
			map.set(car.id, car);
		}
		return map;
	}, [carModels]);

	const closedDateRange = useMemo(
		() => ({
			from: new Date(Date.now() - 86400000),
			to: new Date(Date.now() + 86400000),
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[lang],
	);

	const optionsOrderAll = useMemo<Order.SubscribeOptions>(
		() => ({
			tab: ORDERS_TYPES.ALL,
			lang,
			...(filtersBtns.showOnlyMyOrders &&
				currentUserId && {
					dispatcherIds: [currentUserId],
				}),
			...(filtersBtns.showOrdersWithoutExecutors && {
				hasExecutor: false,
			}),
		}),
		[filtersBtns, currentUserId, lang],
	);

	const optionsOrderClosed = useMemo<Order.SubscribeOptions>(
		() => ({
			tab: ORDERS_TYPES.CLOSED,
			lang,
			withoutRoute: true,
			dateRange: closedDateRange,
		}),
		[closedDateRange, lang],
	);

	const mapOrders = useCallback(
		(orders: Order.Model[], type: OrderType): Order.Model[] =>
			orders.map((order) => {
				const withCars = enrichOrderCars(order, carMap);
				if (type !== ORDERS_TYPES.ALL) return withCars;

				// The server stage stays `executable` after the driver was
				// taken off on some paths (dev check F19): an order that is not
				// executing and has no driver goes by its status instead.
				const staleExecutable =
					order?.stage === Order.ORDERS_STAGE.EXECUTABLE &&
					order.status !== OrderStatus.EXECUTING &&
					!order.executorToOrder?.length;

				if (order?.stage && !staleExecutable) {
					return { ...withCars, stage: order.stage };
				}

				const stage = resolveAllStage(withCars, timeToLive);
				return stage ? { ...withCars, stage } : withCars;
			}),
		[carMap, timeToLive],
	);

	const applyAccessFilters = useCallback(
		(data: Order.Model[], type: OrderType) => {
			let models = subTaxiServiceFilterAccess({
				models: data,
				branchPath: "taxiService.id",
			});
			models = subCarParkFilterAccess({
				models,
				branchPath: "carToOrder",
				branchPath2: "car.park.id",
				isOrderLogic:
					type === ORDERS_TYPES.ALL || type === ORDERS_TYPES.CLOSED,
			});
			return models;
		},
		[subCarParkFilterAccess, subTaxiServiceFilterAccess],
	);

	const distributeAndSetOrders = useCallback(
		(orders: Order.Model[]) => {
			const distributed: Record<
				Exclude<OrderType, "all" | "closed">,
				Order.Model[]
			> = {
				[ORDERS_TYPES.LIVE]: [],
				[ORDERS_TYPES.PRELIMINARY]: [],
				[ORDERS_TYPES.EXECUTABLE]: [],
			};

			for (const model of orders) {
				if (model.stage) {
					distributed[model.stage]?.push(model);
				}
			}

			dispatch(
				orderPage.actions.setOrdersByTab({
					tab: ORDERS_TYPES.ALL,
					data: {
						items: orders,
						count: orders.length,
						pagination: {
							offset: 0,
							count: orders.length,
						},
					},
				}),
			);

			for (const key of Object.keys(distributed) as Array<
				Exclude<OrderType, "all" | "closed">
			>) {
				dispatch(
					orderPage.actions.setOrdersByTab({
						tab: key,
						data: {
							items: distributed[key],
							count: distributed[key].length,
							pagination: {
								offset: 0,
								count: distributed[key].length,
							},
						},
					}),
				);
			}
		},
		[dispatch],
	);

	// null until the first commit: the orders store outlives this component,
	// and an empty window gets no INIT from the server, so starting from []
	// would skip the write and keep the list from the previous visit.
	const prevAll = useRef<Order.Model[] | null>(null);
	const commitAllMapped = useCallback(
		(mapped: Order.Model[]) => {
			if (!canSubscribe) return;
			const models = applyAccessFilters(mapped, ORDERS_TYPES.ALL);
			const prev = prevAll.current;
			const changed =
				!prev ||
				prev.length !== models.length ||
				prev.some((model, i) => model !== models[i]);
			if (!changed) return;
			distributeAndSetOrders(models);
			prevAll.current = models;
		},
		[applyAccessFilters, canSubscribe, distributeAndSetOrders],
	);

	const prevClosed = useRef<Order.Model[] | null>(null);
	const commitClosedMapped = useCallback(
		(mapped: Order.Model[]) => {
			if (!canSubscribe) return;
			const models = applyAccessFilters(mapped, ORDERS_TYPES.CLOSED);
			const prev = prevClosed.current;
			const changed =
				!prev ||
				prev.length !== models.length ||
				prev.some((model, i) => model !== models[i]);
			if (!changed) return;
			dispatch(
				orderPage.actions.setOrdersByTab({
					tab: ORDERS_TYPES.CLOSED,
					data: {
						items: models,
						count: models.length,
						pagination: {
							offset: 0,
							count: models.length,
						},
					},
				}),
			);
			prevClosed.current = models;
		},
		[applyAccessFilters, canSubscribe, dispatch],
	);

	const subAllOrders = useModelSubscribe(optionsOrderAll, Order, {
		debounce: 0,
		onUpdateModels: (raw) => {
			// Sound for a new online order (order settings → notifyOnlineOrders).
			notifyNewOnlineOrders(raw);
			commitAllMapped(mapOrders(raw, ORDERS_TYPES.ALL));
		},
	});
	const subClosedOrders = useModelSubscribe(optionsOrderClosed, Order, {
		debounce: 0,
		onUpdateModels: (raw) => {
			const mapped = mapOrders(raw, ORDERS_TYPES.CLOSED);
			commitClosedMapped(
				ordersType === ORDERS_TYPES.CLOSED
					? mapped.map(Order.hydrateCellsForTable)
					: mapped,
			);
		},
	});

	const allModels = useMemo(
		() => mapOrders(subAllOrders?.models || [], ORDERS_TYPES.ALL),
		[mapOrders, subAllOrders?.models],
	);

	const closedModels = useMemo(() => {
		const mapped = mapOrders(
			subClosedOrders?.models || [],
			ORDERS_TYPES.CLOSED,
		);
		if (ordersType !== ORDERS_TYPES.CLOSED) return mapped;
		return mapped.map(Order.hydrateCellsForTable);
	}, [mapOrders, ordersType, subClosedOrders?.models]);

	useLayoutEffect(() => {
		commitAllMapped(allModels);
	}, [allModels, commitAllMapped]);

	useLayoutEffect(() => {
		commitClosedMapped(closedModels);
	}, [closedModels, commitClosedMapped]);

	return null;
};

declare namespace OrdersDataLoader {
	interface Props {
		carModels: Car.Model[];
	}
}

export default memo(OrdersDataLoader);
